<template>
  <button
    class="theme-selector-card w-full"
    :aria-selected="isSelected"
    :qaid="`theme-card-${theme.marketingThemeGroup}`"
    @click="$emit('theme-clicked', theme)"
  >
    <pdl-card>
      <template #bodyContent>
        <div
          class="theme-details bg-gray-05 transition-colors relative flex flex-row z-10"
          :class="{selected: isSelected}"
        >
          <div ref="imageContainer" class="w-1/3 max-w-40 relative overflow-hidden">
            <img
              v-if="themeImage"
              class="absolute inset-0 object-cover w-full h-full"
              :src="themeImage"
              alt=""
              :qaid="`theme-card-image-${theme.id}`"
            />
          </div>
          <div class="flex-col py-1 px-[.625rem] w-full">
            <div class="flex justify-between pb-1">
              <div :qaid="`theme-card-title-${theme.id}`" class="text-xs font-bold">
                {{ theme.marketingThemeGroup }}
              </div>
              <div :qaid="`theme-card-price-difference-${theme.id}`" class="text-xs">
                {{ formattedPriceRange }}
              </div>
            </div>
            <p class="text-xs" :qaid="`theme-card-description-${theme.id}`">
              {{ theme.marketingCopy }}
            </p>
          </div>
        </div>
      </template>
    </pdl-card>
  </button>
</template>

<script>
import {PdlCard} from '@pedal/pdl-card';
import {Cloudinary, CloudinaryConstants} from '@/utils/image/cloudinary-helpers';
import {mapGetters, mapState} from 'vuex';
import {formatThemeInspirationsPriceRange} from '@/familyDetailsPage/helpers/formatInspirationPriceRange.js';

export default {
  components: {
    PdlCard,
  },
  props: {
    theme: {
      type: Object,
      required: true,
    },
  },
  emits: ['theme-clicked'],
  computed: {
    ...mapState('bikeCustomization', ['bikeData']),
    ...mapGetters('bikeCustomization', ['selectedInspirationId']),

    themeImage() {
      const imageTransformations = {
        w: 200,
        h: 200,
        c: 'fill',
        dpr: `${window.devicePixelRatio}`,
        ...CloudinaryConstants.DEFAULT_PARAMETERS,
      };

      return this.theme.imageThumbnailReference
        ? Cloudinary.createAssetUrl(this.theme.imageThumbnailReference, imageTransformations)
        : '';
    },
    isSelected() {
      return this.theme?.inspirations?.some(
        (inspiration) => inspiration.p1PresetThemeChoiceId === this.selectedInspirationId
      );
    },
    formattedPriceRange() {
      return formatThemeInspirationsPriceRange(this.theme);
    },
  },
};
</script>

<style lang="scss" scoped>
.theme-selector-card {
  transition: scale 0.1s ease-in, box-shadow 0.2s ease-in;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);
  scale: 1;

  &:active {
    scale: 0.99;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-100);
    outline-offset: 2px;
  }
}

.pdl-card {
  margin-bottom: 0;
}

:deep(.pdl-card__body) {
  padding: 0;
}

.theme-details {
  transition: background-color 0.2s ease, border 0.2s ease;
  border: 0.125rem solid transparent;

  &.selected {
    border-color: var(--blue-100, #007ab8);
    box-shadow: 0 7px 6px -6px rgba(0, 0, 0, 0.25), 0 14px 15px -13px rgba(0, 0, 0, 0.3);
  }

  @media (hover: hover) {
    &:hover {
      background-color: var(--blue-10, #e6f2f8);
      border-color: var(--blue-100, #007ab8);
    }
  }
}

.theme-selector-card:hover .theme-details {
  background-color: var(--blue-10, #e6f2f8);
}
</style>
